import { useDatePickerLocale } from 'hooks/useDatePickerLocale'
import { useSelector } from 'react-redux'
import React, { useState } from 'react'
import { format } from 'date-fns'
import { RootState } from 'constants/interfaces'
import { DateRange, Range } from 'react-date-range'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, Flex } from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { CalendarDays } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'

export interface DateRangeState {
  startDate: Date | null
  endDate: Date | null
}

interface DatePickerProps {
  dateRange: DateRangeState
  onDateRangeChange: (newDateRange: DateRangeState) => void
}

const setStartOfDay = (date: Date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

const setEndOfDay = (date: Date) => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

const DEFAULT_DATE_RANGE = {
  startDate: new Date(),
  endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
  key: 'selection',
}

export const AdminDateRangePicker: React.FC<DatePickerProps> = ({ dateRange, onDateRangeChange }) => {
  const { windowWidth } = useWindowDimensions()
  const { theme } = useSelector((state: RootState) => state.general)
  const datePickerLocale = useDatePickerLocale()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [localDateRange, setLocalDateRange] = useState<Range>({
    startDate: dateRange.startDate ? setStartOfDay(dateRange.startDate) : setStartOfDay(DEFAULT_DATE_RANGE.startDate),
    endDate: dateRange.endDate ? setEndOfDay(dateRange.endDate) : setEndOfDay(DEFAULT_DATE_RANGE.endDate),
    key: DEFAULT_DATE_RANGE.key,
  })

  const t_default_dates = useLocaleText('default_dates', true)
  const t_select_range = useLocaleText('select_range', true)

  const handleDefaultDates = async () => {
    const defaultRange = {
      startDate: null,
      endDate: null,
    }
    setLocalDateRange(DEFAULT_DATE_RANGE)
    onDateRangeChange(defaultRange)
    onClose()
  }

  const handleSubmit = () => {
    if (localDateRange.startDate && localDateRange.endDate) {
      const newDateRange = {
        startDate: setStartOfDay(localDateRange.startDate),
        endDate: setEndOfDay(localDateRange.endDate),
      }
      onDateRangeChange(newDateRange)
    }
    onClose()
  }

  const maxDate = localDateRange.startDate
    ? new Date(localDateRange.startDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    : undefined

  const outputFormat = theme?.is_support_us_date_format ? 'MM/dd/yyyy' : 'dd/MM/yyyy'

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <NoFocusButton border="1px solid" borderColor="gray.300" borderRadius="5px" fontSize="14px">
          {dateRange.startDate && dateRange.endDate ? (
            <Flex alignItems="center" style={{ gap: '5px' }}>
              <CalendarDays size={14} />
              {`${format(dateRange.startDate, outputFormat)} - ${format(dateRange.endDate, outputFormat)}`}
            </Flex>
          ) : (
            'Choose dates'
          )}
        </NoFocusButton>
      </PopoverTrigger>
      <PopoverContent
        width="fit-content"
        minW={windowWidth > 768 ? '500px' : '350px'}
        p={0}
        boxShadow="md"
        zIndex={99999}
        _focus={{ outline: 'none', boxShadow: 'none' }}
      >
        <PopoverBody p={0}>
          <DateRange
            ranges={[localDateRange]}
            onChange={(item) => {
              if (item.selection) {
                setLocalDateRange(item.selection)
              }
            }}
            months={2}
            direction={windowWidth > 768 ? 'horizontal' : 'vertical'}
            maxDate={maxDate}
            locale={datePickerLocale}
            showDateDisplay={false}
            showMonthAndYearPickers={true}
            className="byDateSelect"
            moveRangeOnFirstSelection={true}
            color={theme?.elementsColor}
            rangeColors={[theme?.backgroundLight!]}
            css={{
              width: '100%',
              direction: 'ltr',
              '.rdrCalendarWrapper': {
                width: '100%',
              },
              '.rdrDayNumber': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              },
              '.rdrEndEdge': {
                right: '20px',
              },
            }}
          />
          <div style={{ padding: '8px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <NoFocusButton borderRadius="5px" onClick={handleDefaultDates} size="sm">
              {t_default_dates}
            </NoFocusButton>
            <NoFocusButton
              borderRadius="5px"
              onClick={handleSubmit}
              isDisabled={!localDateRange.startDate || !localDateRange.endDate}
              size="sm"
            >
              {t_select_range}
            </NoFocusButton>
          </div>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
